import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import PanelTemplate from "../../templates/Panel";
import PageHeader from "../../components/PageHeader";
import Box from "../../components/Box";
import Button from "../../components/Button";
import Avatar from "../../components/Avatar";
import BoxKpi from "../../components/dashboard/BoxKpi";
import CreditCoin from "../../components/CreditCoin";
import ModalBuyCredits from "../../components/ModalBuyCredits";
import api from "../../services/api";
import { hasPermission } from "../../services/auth";
import { refreshCredits } from "../../services/credits";
import { renderError } from "../../helpers/errors";

import "./styles.scss";

const shortDate = value => new Date(`${value}T00:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

/** Gráfico de barras do consumo diário. SVG direto: são 14 valores. */
const DailyChart = ({ data }) => {
    const max = Math.max(1, ...data.map(item => item.credits));
    const step = 40;
    const width = Math.max(320, data.length * step + 30);
    const barWidth = 24;

    return (
        <svg className="daily-chart" viewBox={`0 0 ${width} 180`} role="img"
            aria-label={`Consumo diário de créditos: máximo de ${max} em um dia`}>
            <line className="axis" x1="30" y1="145" x2={width - 10} y2="145" />
            {[0.33, 0.66, 1].map(ratio => (
                <line key={ratio} className="grid" x1="30" y1={145 - ratio * 115} x2={width - 10} y2={145 - ratio * 115} strokeDasharray="3 4" />
            ))}
            <text x="24" y="148" textAnchor="end">0</text>
            <text x="24" y={148 - 115} textAnchor="end">{max}</text>

            {data.map((item, index) => {
                const height = (item.credits / max) * 115;
                const x = 30 + index * step;
                const last = index === data.length - 1;

                return (
                    <g key={item.date}>
                        <rect
                            x={x}
                            y={145 - height}
                            width={barWidth}
                            height={Math.max(height, item.credits > 0 ? 2 : 0)}
                            rx="3"
                            fill="#00C2B3"
                            fillOpacity={last ? 1 : 0.6}
                        />
                        {index % 3 === 0 &&
                            <text x={x + barWidth / 2} y="163" textAnchor="middle">{shortDate(item.date)}</text>
                        }
                    </g>
                );
            })}
        </svg>
    );
}

export const CreditOverview = () => {
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState(null);
    const [features, setFeatures] = useState([]);
    const [showBuy, setShowBuy] = useState(false);

    const navigate = useNavigate();
    const canManage = hasPermission('manage_credits');

    useEffect(() => {
        load();
    }, []);

    const load = () => {
        setLoading(true);

        Promise.all([
            api.get('/credit/summary'),
            api.get('/credit')
        ]).then(([summaryRes, walletRes]) => {
            setSummary(summaryRes.data.resource);
            setFeatures(walletRes.data.resource.features ?? []);
        }).catch(renderError).then(() => setLoading(false));
    }

    const featureLabel = key => features.find(item => item.key === key)?.label ?? key;

    const maxFeatureCredits = Math.max(1, ...(summary?.by_feature ?? []).map(item => item.credits));

    return (
        <>
            <ModalBuyCredits
                show={showBuy}
                close={() => setShowBuy(false)}
                onPurchased={() => { refreshCredits(); load(); }}
            />

            <PanelTemplate id="credit-overview">
                <PageHeader
                    left={<h1>Créditos</h1>}
                    right={canManage &&
                        <>
                            <Button type={`secondary`} size={`small`} svg={`people`} text={`Distribuição`} action={() => navigate('/credits/distribuicao')} />
                            <Button type={`secondary`} size={`small`} svg={`refresh`} text={`Recarga automática`} action={() => navigate('/credits/recarga')} />
                            <Button type={`primary`} size={`small`} svg={`plus-white`} text={`Comprar créditos`} action={() => setShowBuy(true)} />
                        </>
                    }
                />

                <div className="credit-kpis">
                    <BoxKpi
                        icon={<CreditCoin size={24} />}
                        accent={`teal`}
                        label={`Saldo disponível`}
                        value={(summary?.balance ?? 0).toLocaleString('pt-BR')}
                        hint={summary?.reserved > 0
                            ? `${summary.reserved} reservados em gerações rodando`
                            : 'nenhuma geração em andamento'}
                        loading={loading}
                    />
                    <BoxKpi
                        icon={`zap`}
                        accent={`coral`}
                        label={`Consumo no mês`}
                        value={(summary?.totals?.consumed ?? 0).toLocaleString('pt-BR')}
                        hint={`${summary?.totals?.operations ?? 0} ${summary?.totals?.operations === 1 ? 'operação' : 'operações'}`}
                        loading={loading}
                    />
                    <BoxKpi
                        icon={`chart`}
                        accent={`cobalt`}
                        label={`Média por dia`}
                        value={(summary?.projection?.daily_average ?? 0).toLocaleString('pt-BR')}
                        hint={`últimos 14 dias`}
                        loading={loading}
                    />
                    <BoxKpi
                        icon={`alert`}
                        accent={`amber`}
                        label={`Saldo acaba em`}
                        value={summary?.projection
                            ? new Date(`${summary.projection.runs_out_at}T00:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
                            : '—'}
                        hint={summary?.projection ? 'no ritmo atual' : 'sem consumo para projetar'}
                        loading={loading}
                    />
                </div>

                <div className="credit-columns">
                    <Box title={`Consumo diário · últimos 14 dias`}>
                        {summary && summary.daily.every(item => item.credits === 0) &&
                            <p className="empty-note">Nenhum crédito consumido nos últimos 14 dias.</p>
                        }
                        {summary && summary.daily.some(item => item.credits > 0) && <DailyChart data={summary.daily} />}
                    </Box>

                    <Box title={`Em que os créditos foram usados`}>
                        {!loading && (summary?.by_feature ?? []).length === 0 &&
                            <p className="empty-note">Nenhum recurso consumido neste período.</p>
                        }
                        {(summary?.by_feature ?? []).map(item => (
                            <div className="bar-row" key={item.feature_key}>
                                <span className="bar-label">{featureLabel(item.feature_key)}</span>
                                <span className="bar-track">
                                    <i style={{ width: `${(item.credits / maxFeatureCredits) * 100}%` }}></i>
                                </span>
                                <span className="bar-value">{item.credits} · {item.uses}×</span>
                            </div>
                        ))}
                    </Box>
                </div>

                <Box
                    title={`Consumo por pessoa`}
                    className={`no-padding`}
                    tools={<Link className="box-link" to="/credits/extrato">Ver extrato completo</Link>}
                >
                    <div className="table-responsive">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Pessoa</th>
                                    <th>Nível de acesso</th>
                                    <th className="right">Consumo no mês</th>
                                    <th className="right">Último uso</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading &&
                                    <tr><td colSpan={4} className="no-data">Carregando…</td></tr>
                                }
                                {!loading && (summary?.by_admin ?? []).length === 0 &&
                                    <tr><td colSpan={4} className="no-data">Ninguém consumiu crédito neste período.</td></tr>
                                }
                                {(summary?.by_admin ?? []).map(item => (
                                    <tr key={item.admin_id}>
                                        <td>
                                            <span className="person">
                                                <Avatar name={item.name} photo={item.photo_url} size={30} />
                                                <span>
                                                    {item.name}
                                                    {!item.active && <span className="person-note">usuário removido</span>}
                                                    {item.email && <span className="person-note">{item.email}</span>}
                                                </span>
                                            </span>
                                        </td>
                                        <td>{item.role ?? '—'}</td>
                                        <td className="right">{item.credits.toLocaleString('pt-BR')}</td>
                                        <td className="right">{new Date(item.last_use).toLocaleString('pt-BR')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Box>
            </PanelTemplate>
        </>
    );
}

export default CreditOverview;
